import { useTranslation } from 'react-i18next'
import { shallowEqual, useSelector } from 'react-redux'
import { format } from 'date-fns'

import useTableSort from '@/hooks/table/use-table-sort.hook'
import { CurrencyCodeEnum, TimeZoneUTCType } from '@/enums'
import { getFormatNumber, getDateTimeFromIsoUTC } from '@/utils'

import { reportsSelector } from '@/state/reports/slices/index.slices'
import { uiGlobalSelector } from '@/state/ui/global/slices/index.slices'
import { dateTimeTableFormat } from '@/constants/date'

import { TableHeadCellPropsType } from '@/state/ui/global/models'
import {
  RoundsReportData,
  RoundsReportLibraryColumnProps,
} from '@/state/reports/models'

const useRoundsReports = () => {
  const { t } = useTranslation()

  const { roundsReports, roundsPagination, isLoading } = useSelector(
    reportsSelector,
    shallowEqual,
  )

  const { isLoadingFull } = useSelector(uiGlobalSelector)

  const headCells: TableHeadCellPropsType<RoundsReportLibraryColumnProps>[] = [
    { id: 'internalTransactionId', label: t('reports.internalTransactionId') },
    { id: 'playerId', label: t('labels.playerId') },
    { id: 'username', label: t('labels.username') },
    { id: 'gameProviderName', label: t('labels.gameProvider') },
    { id: 'gameCategoryName', label: t('labels.gameCategory') },
    { id: 'gameName', label: t('labels.game') },
    { id: 'transactionTypePayment', label: t('labels.transactionTypePayment') },
    { id: 'debitTime', label: t('reports.debitTime') },
    { id: 'creditTime', label: t('reports.creditTime') },
    { id: 'bonusMoneyBet', label: t('reports.bonusMoneyBet') },
    { id: 'bonusMoneyWin', label: t('reports.bonusMoneyWin') },
    { id: 'balanceAfter', label: t('reports.bonusBalanceAfter') },
    { id: 'providerTransactionId', label: t('reports.providerTransactionId') },
    { id: 'providerRoundId', label: t('reports.providerRoundId') },
    /* TODO: This implementation was hide and maybe use later in the future
    {
      id: 'wageringContribution',
      label: t('labels.wageringContribution'),
    }, */
  ]

  const headXlsxCells: TableHeadCellPropsType<RoundsReportLibraryColumnProps>[] =
    [...headCells, { id: 'currency', label: t('labels.currency') }]

  const getFormattedRow = (
    report: RoundsReportData,
  ): RoundsReportLibraryColumnProps => {
    return {
      ...report,
      debitTime: report.debitTime
        ? `${format(
            getDateTimeFromIsoUTC(
              report.debitTime.toString(),
              TimeZoneUTCType['GMT-04 Eastern Daylight Time'],
            ),
            dateTimeTableFormat,
          )} ET`
        : '-',
      creditTime: report.creditTime
        ? `${format(
            getDateTimeFromIsoUTC(
              report.creditTime.toString(),
              TimeZoneUTCType['GMT-04 Eastern Daylight Time'],
            ),
            dateTimeTableFormat,
          )} ET`
        : '-',
      transactionTypePayment:
        report.transactionTypePayment === 1
          ? t('reports.transactionTypePayment.REAL_MONEY')
          : t('reports.transactionTypePayment.BONUS_MONEY'),
      bonusMoneyBet: report.bonusMoneyBet ? Number(report.bonusMoneyBet) : 0,
      bonusMoneyWin: report.bonusMoneyWin ? Number(report.bonusMoneyWin) : 0,
      balanceAfter: report.balanceAfter ? Number(report.balanceAfter) : 0,
      bonusMoneyBetText: report.bonusMoneyBet
        ? getFormatNumber(
            Number(report.bonusMoneyBet),
            report.currency || CurrencyCodeEnum.USD,
          )
        : '-',
      bonusMoneyWinText: report.bonusMoneyWin
        ? getFormatNumber(
            Number(report.bonusMoneyWin),
            report.currency || CurrencyCodeEnum.USD,
          )
        : '-',
      balanceAfterText: report.balanceAfter
        ? getFormatNumber(
            Number(report.balanceAfter),
            report.currency || CurrencyCodeEnum.USD,
          )
        : '-',
    }
  }

  const formattedReports =
    roundsReports?.map((report) => {
      return getFormattedRow(report)
    }) || []

  const sortedInitialState = 'playerId'

  const { sortedRows, createSortHandler, order, orderBy } = useTableSort<
    RoundsReportLibraryColumnProps,
    keyof RoundsReportLibraryColumnProps
  >(formattedReports, sortedInitialState, 'desc')

  return {
    sortedRows,
    createSortHandler,
    order,
    orderBy,
    roundsPagination,
    isLoading,
    isLoadingFull,
    headCells,
    headXlsxCells,
    getFormattedRow,
  }
}

export default useRoundsReports
